import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { fmtCHF } from '../utils/format';
import { TagRevenu } from '../types';
import { BadgeTagRevenu } from '../components/TagScenarioRevenu';

/**
 * Mois-places : une place occupée un mois (décimales = prorata d'entrée/sortie).
 * Tolère l'absence de valeur : cette carte ne doit jamais faire tomber l'arbre
 * React — sans error boundary, une exception ici efface jusqu'à l'écran de
 * connexion (arrivé le 19.08.2026, serveur lancé sans --watch servant encore
 * l'ancienne forme du résumé au nouveau bundle).
 */
const fmtNb = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('fr-CH', { maximumFractionDigits: 2 });
const signe = (v: number | null | undefined) =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${fmtNb(v)}`;

interface ChangementRecent {
  module: string;
  cible: string;
  valeur: string | null;
  tag: string | null;
  commentaire: string;
  auteur: string | null;
  creeLe: string;
}

interface Resume {
  annee: number;
  salaires: { nbEmployes: number; totalAnnuel: number; tauxChargesSociales: number };
  revenus: { totalAnnuel: number; nbFlux: number; retrocessionsARisque: number };
  mesures: {
    programmes: Array<{
      programme: string;
      places: number;
      moisFactures: number;
      moisPlacesFacturees: number;
      droitEcoule: number;
      ecart: number;
      moisPlacesRestantes: number;
      rythmeRestant: number;
      rythmeActuel: number;
      convention: number;
      risque: number;
    }>;
  };
  loyers: { totalAnnuel: number; totalLoyer: number; totalCharges: number; nbSites: number };
  referentiels: { clients: number; contrats: number; scenarios: number };
  changements: ChangementRecent[];
  validations: unknown[];
}

/**
 * Page d'accueil : chiffres clés par module, derniers changements et
 * validations en attente. L'accès différencié par profil (quels modules pour
 * quel·le utilisateur·rice) et le circuit d'approbation seront branchés ici.
 */
export function AccueilPage() {
  const annee = new Date().getFullYear();
  const [resume, setResume] = useState<Resume | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(() => {
    setErreur(null);
    api.get<Resume>(`/accueil/resume?annee=${annee}`)
      .then(setResume)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee]);

  useEffect(() => { charger(); }, [charger]);

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Accueil</h1>
          <span className="stat-pill">budget {annee}</span>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      {resume && (
        <>
          {/* ── Résumés des modules ─────────────────────────────────────── */}
          <div className="kpi-row mb-16">
            <a href="#revenus" className="kpi-card" style={{ textDecoration: 'none' }} title="Ouvrir le module Revenus">
              <span className="kpi-card__label">Revenus — total {annee} (décisions)</span>
              <span className="kpi-card__value">{fmtCHF(resume.revenus.totalAnnuel)}<span className="kpi-card__suffix">CHF</span></span>
              <span className="text-xs text-ink-3">
                {resume.revenus.nbFlux} flux
                {resume.revenus.retrocessionsARisque > 0 && (
                  <span className="badge badge-err badge-sm" style={{ marginLeft: 6 }}>
                    {resume.revenus.retrocessionsARisque} rétrocession{resume.revenus.retrocessionsARisque > 1 ? 's' : ''} à risque
                  </span>
                )}
              </span>
            </a>
            <a href="#salaires" className="kpi-card" style={{ textDecoration: 'none' }} title="Ouvrir le module Salaires">
              <span className="kpi-card__label">Salaires — masse {annee} (brut)</span>
              <span className="kpi-card__value">{fmtCHF(resume.salaires.totalAnnuel)}<span className="kpi-card__suffix">CHF</span></span>
              <span className="text-xs text-ink-3">{resume.salaires.nbEmployes} employé·es · charges {Math.round(resume.salaires.tauxChargesSociales * 100)} %</span>
            </a>
            <a href="#mesures" className="kpi-card" style={{ textDecoration: 'none' }} title="Ouvrir le module Mesures">
              <span className="kpi-card__label">Mesures — places conventionnées {annee}</span>
              <span className="kpi-card__value" style={{ fontSize: 'var(--t-16)' }}>
                {resume.mesures.programmes.map((p) => (
                  <span
                    key={p.programme}
                    style={{ display: 'block' }}
                    title={`${p.programme} : ${fmtNb(p.moisPlacesFacturees)} mois-places décomptés sur `
                      + `${p.moisFactures} mois, pour un droit de ${fmtNb(p.droitEcoule)} `
                      + `(${p.places} places) → ${signe(p.ecart)}.\n`
                      + `Il reste ${fmtNb(p.moisPlacesRestantes)} mois-places d'ici décembre pour atteindre `
                      + `${p.convention}, soit ${fmtNb(p.rythmeRestant)}/mois — rythme observé `
                      + `${fmtNb(p.rythmeActuel)}/mois.\n`
                      + `En dessous : risque de rétrocession si des individualisées sont vendues. `
                      + `Au-dessus : sur-utilisation, sans revenu supplémentaire.`}
                  >
                    {p.programme}{' '}
                    <span style={{ color: p.ecart < 0 ? 'var(--warn)' : 'var(--ok)' }}>{signe(p.ecart)}</span>
                    <span className="text-xs text-ink-3">
                      {' · reste '}{fmtNb(p.moisPlacesRestantes)}{' → '}{fmtNb(p.rythmeRestant)}/mois
                    </span>
                  </span>
                ))}
              </span>
              <span className="text-xs text-ink-3">
                écart sur les mois décomptés · reste à consommer d'ici décembre
              </span>
            </a>
            <a href="#loyers" className="kpi-card" style={{ textDecoration: 'none' }} title="Ouvrir le module Loyers">
              <span className="kpi-card__label">Loyers — total {annee}</span>
              <span className="kpi-card__value">{fmtCHF(resume.loyers.totalAnnuel)}<span className="kpi-card__suffix">CHF</span></span>
              <span className="text-xs text-ink-3">
                {resume.loyers.nbSites} site{resume.loyers.nbSites > 1 ? 's' : ''}
                {resume.loyers.totalCharges > 0 ? ` · dont charges ${fmtCHF(resume.loyers.totalCharges)}` : ''}
              </span>
            </a>
            <a href="#contrats" className="kpi-card" style={{ textDecoration: 'none' }} title="Ouvrir les référentiels">
              <span className="kpi-card__label">Référentiels</span>
              <span className="kpi-card__value" style={{ fontSize: 'var(--t-16)' }}>
                {resume.referentiels.clients} clients · {resume.referentiels.contrats} contrats
              </span>
              <span className="text-xs text-ink-3">{resume.referentiels.scenarios} scénario{resume.referentiels.scenarios > 1 ? 's' : ''} actif{resume.referentiels.scenarios > 1 ? 's' : ''}</span>
            </a>
          </div>

          <div className="flex gap-16 flex-wrap" style={{ alignItems: 'flex-start' }}>
            {/* ── Changements récents ───────────────────────────────────── */}
            <div className="card p-16" style={{ flex: 2, minWidth: 420 }}>
              <h2 className="text-base font-semi mb-8" style={{ marginTop: 0 }}>Changements récents</h2>
              <table className="table table-compact">
                <thead>
                  <tr><th>Quand</th><th>Module</th><th>Objet</th><th>Valeur</th><th>Statut</th><th>Par</th></tr>
                </thead>
                <tbody>
                  {resume.changements.map((c, i) => (
                    <tr key={i} title={c.commentaire}>
                      <td className="tabular-nums text-xs">{fmtDate(c.creeLe)}</td>
                      <td><span className="badge badge-sm">{c.module}</span></td>
                      <td>{c.cible}</td>
                      <td className="text-sm">{c.valeur ?? '—'}</td>
                      <td>{c.tag ? <BadgeTagRevenu tag={c.tag as TagRevenu} /> : '—'}</td>
                      <td className="text-sm">{c.auteur ?? <span className="text-ink-3">import</span>}</td>
                    </tr>
                  ))}
                  {resume.changements.length === 0 && (
                    <tr><td colSpan={6} className="text-ink-3">Aucun changement enregistré.</td></tr>
                  )}
                </tbody>
              </table>
              <p className="text-xs text-ink-3" style={{ marginBottom: 0 }}>
                Survoler une ligne pour lire le commentaire. Le journal complet (avec suppressions) est dans
                l'audit, accessible aux admins.
              </p>
            </div>

            {/* ── Validations en attente ────────────────────────────────── */}
            <div className="card p-16" style={{ flex: 1, minWidth: 300 }}>
              <h2 className="text-base font-semi mb-8" style={{ marginTop: 0 }}>Validations en attente</h2>
              {resume.validations.length === 0 && (
                <div className="text-sm text-ink-2">
                  <p style={{ marginTop: 0 }}>
                    <span className="badge badge-ok badge-sm">aucune validation en attente</span>
                  </p>
                  <p className="text-xs text-ink-3">
                    Le circuit d'approbation arrive : certains changements devront être validés avant de faire
                    foi — par exemple la modification du montant d'une facturation mensuelle, soumise à la
                    comptabilité. Les demandes en attente s'afficheront ici, avec l'accès différencié par
                    profil d'utilisateur·rice.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
